import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/hooks/use-toast'
import { useCreateTenant } from './hooks/useCreateTenant'
import {
  getCreateTenantSchema,
  type CreateTenantFormData,
} from './schemas/createTenantSchema'

interface CreateTenantDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Remove consecutive hyphens
}

export function CreateTenantDialog({ open, onOpenChange }: CreateTenantDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const createTenant = useCreateTenant()

  const schema = useMemo(() => getCreateTenantSchema(t), [t])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CreateTenantFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      slug: '',
      domain: '',
      logoUrl: '',
      adminEmail: '',
      adminName: '',
    },
  })

  const name = watch('name')

  // Auto-generate slug from name
  useEffect(() => {
    if (name) {
      const generatedSlug = slugify(name)
      setValue('slug', generatedSlug, { shouldValidate: true })
    }
  }, [name, setValue])

  const onSubmit = async (data: CreateTenantFormData) => {
    try {
      const result = await createTenant.mutateAsync(data)

      const successMessage = result.admin.isNewUser
        ? t('admin.tenants.create.successNewUser', { name: result.tenant.name, password: result.admin.temporaryPassword })
        : t('admin.tenants.create.successExistingUser', { name: result.tenant.name })

      toast({
        title: t('admin.tenants.create.success'),
        description: successMessage,
      })

      reset()
      onOpenChange(false)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('admin.tenants.create.error'),
        description: error instanceof Error ? error.message : t('errors.unknown'),
      })
    }
  }

  const handleClose = () => {
    if (!createTenant.isPending) {
      reset()
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('admin.tenants.create.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.tenants.create.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('admin.tenants.create.companyName')} *</Label>
              <Input
                id="name"
                placeholder={t('admin.tenants.create.companyNamePlaceholder')}
                {...register('name')}
                disabled={createTenant.isPending}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">{t('admin.tenants.create.slug')} *</Label>
              <Input
                id="slug"
                placeholder={t('admin.tenants.create.slugPlaceholder')}
                {...register('slug')}
                disabled={createTenant.isPending}
              />
              {errors.slug && (
                <p className="text-sm text-destructive">{errors.slug.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {t('admin.tenants.create.slugHelp')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="domain">{t('admin.tenants.create.domain')}</Label>
              <Input
                id="domain"
                placeholder={t('admin.tenants.create.domainPlaceholder')}
                {...register('domain')}
                disabled={createTenant.isPending}
              />
              {errors.domain && (
                <p className="text-sm text-destructive">{errors.domain.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="logoUrl">{t('admin.tenants.create.logoUrl')}</Label>
              <Input
                id="logoUrl"
                placeholder={t('admin.tenants.create.logoUrlPlaceholder')}
                {...register('logoUrl')}
                disabled={createTenant.isPending}
              />
              {errors.logoUrl && (
                <p className="text-sm text-destructive">{errors.logoUrl.message}</p>
              )}
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="text-sm font-medium">{t('admin.tenants.create.adminSection')}</h4>

            <div className="space-y-2">
              <Label htmlFor="adminEmail">{t('admin.tenants.create.adminEmail')} *</Label>
              <Input
                id="adminEmail"
                type="email"
                placeholder={t('admin.tenants.create.adminEmailPlaceholder')}
                {...register('adminEmail')}
                disabled={createTenant.isPending}
              />
              {errors.adminEmail && (
                <p className="text-sm text-destructive">{errors.adminEmail.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminName">{t('admin.tenants.create.adminName')}</Label>
              <Input
                id="adminName"
                placeholder={t('admin.tenants.create.adminNamePlaceholder')}
                {...register('adminName')}
                disabled={createTenant.isPending}
              />
              {errors.adminName && (
                <p className="text-sm text-destructive">{errors.adminName.message}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createTenant.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={createTenant.isPending}>
              {createTenant.isPending ? t('common.creating') : t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
